import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getPblLawyerCvReviewQueue } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import DataTable from '@/components/DataTable';
import { Button } from '@/components/UI/Button';
import { LoadingPage } from '@/layout';
import PATH from '@/router/path';
import type { PblLawyerCvRevision } from '@/types/pbl';
import { errorHandler } from '@/utils/toastHandler';

import { EmptyState, QueuePanel, QueueTableScroll } from './styles';

type CvRevisionTableRow = {
  id: string;
  name: string;
  revision: PblLawyerCvRevision;
};

const PblVettingCvRevisions = () => {
  const navigate = useNavigate();
  // @ts-ignore -- the project's generated i18next resource type exceeds CRA's compiler recursion limit in this view.
  const { t } = useTranslation();
  const [rows, setRows] = useState<PblLawyerCvRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const tr = useCallback(
    (key: string, defaultValue: string, options?: Record<string, unknown>) =>
      t(key as any, { defaultValue, ...options }) as string,
    [t]
  );

  useEffect(() => {
    let mounted = true;

    const loadQueue = async () => {
      setLoading(true);

      try {
        const response = await getPblLawyerCvReviewQueue({ page: 1, per_page: 50 });

        if (response.data.success === false) {
          errorHandler(
            response.data.message ||
              tr('pbl.backlogEnhancements.cvReview.queueLoadFailed', 'Unable to load CV review queue.')
          );
          return;
        }

        if (mounted) setRows(response.data.data.data || []);
      } catch (error: any) {
        if (mounted) {
          errorHandler(
            error.response?.data?.message ||
              error.response?.data?.data ||
              tr('pbl.backlogEnhancements.cvReview.queueLoadFailed', 'Unable to load CV review queue.')
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadQueue();

    return () => {
      mounted = false;
    };
  }, [tr]);

  const tableRows = useMemo<CvRevisionTableRow[]>(
    () =>
      rows.map((revision) => ({
        id: String(revision.id),
        name:
          revision.lawyer_profile?.full_name_en?.trim() ||
          revision.lawyer_profile?.full_name_zh?.trim() ||
          tr('pbl.backlogEnhancements.cvReview.lawyerFallback', `Lawyer #${revision.pbl_lawyer_profile_id}`, {
            id: revision.pbl_lawyer_profile_id,
          }),
        revision,
      })),
    [rows, tr]
  );

  const columns = useMemo<ColumnDef<CvRevisionTableRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: tr('pbl.backlogEnhancements.cvReview.columns.name', 'Name'),
        meta: {
          headerStyle: { width: '70%' },
          cellStyle: { width: '70%' },
          mobileLabel: tr('pbl.backlogEnhancements.cvReview.columns.name', 'Name'),
        },
      },
      {
        id: 'actions',
        header: tr('pbl.backlogEnhancements.cvReview.columns.actions', 'Actions'),
        meta: {
          headerStyle: { width: '30%' },
          cellStyle: { width: '30%' },
          mobileLabel: tr('pbl.backlogEnhancements.cvReview.columns.actions', 'Actions'),
        },
        cell: ({ row }) => (
          <Button
            type="button"
            size="sm"
            variant="primary"
            rounded={false}
            onClick={() =>
              navigate(PATH.pbl.vettingCvRevisionDetail.replace(':revisionId', String(row.original.revision.id)))
            }
          >
            {tr('pbl.backlogEnhancements.cvReview.review', 'Review')}
          </Button>
        ),
        enableSorting: false,
      },
    ],
    [navigate, tr]
  );

  return (
    <PageContainer title={tr('pbl.backlogEnhancements.cvReview.queueTitle', 'CV review queue')}>
      <LoadingPage isLoading={loading} />
      <QueuePanel>
        <QueueTableScroll>
          <DataTable
            columns={columns}
            data={tableRows}
            canSearch={tableRows.length > 0}
            hidePagination={tableRows.length === 0}
            mobileCardLayout
          />
        </QueueTableScroll>
        {!loading && tableRows.length === 0 && (
          <EmptyState>{tr('pbl.backlogEnhancements.cvReview.nonePending', 'No pending CV revisions.')}</EmptyState>
        )}
      </QueuePanel>
    </PageContainer>
  );
};

export default PblVettingCvRevisions;
